// @ts-check
import { gql } from '@apollo/client/core';
import { Image, View } from '@react-pdf/renderer';
import React from 'react';
import {
  Company,
  Page,
  Product,
  QRCode,
  Text,
  Title,
} from '../../pdf-design-system';
import { PagesProvider } from '../../pdf-design-system/Base/PageLayout/Page';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import gothic400 from './fonts/gothic400';
import gothic700 from './fonts/gothic700';
import messages from './locale/messages';

const productCountFirstPage = 9;
const productCountOtherPages = 12;

export default function ABSWineAgenciesCatalogue({
  data,
  locale,
  options = { bleed: 0 },
  ...rest
}) {
  const sections = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((section, index) => {
          const { registration } = section;
          const { firstPageProducts, otherProducts } = paginateProducts({
            products: registration.registeredProducts.nodes,
          });

          const otherPages = Math.ceil(
            otherProducts?.length / productCountOtherPages
          );
          return (
            <React.Fragment key={`wrapper-${index}`}>
              <CatalogPage
                key={`first-${index}`}
                registration={registration}
                theme={theme}
                bleed={options.bleed}
                {...rest}
              >
                <PageBody>
                  <View>
                    <ExhibitorHeader
                      registration={registration}
                      style={{ marginBottom: 9 }}
                    />
                    <TableHeader />
                    <View style={{ height: 465, display: 'flex' }}>
                      {firstPageProducts?.map((registeredProduct, index) => (
                        <ProductRow
                          key={registeredProduct.productId}
                          registeredProduct={registeredProduct}
                          index={index + 1}
                          locale={locale}
                          isFirst={index === 0}
                        />
                      ))}
                    </View>
                  </View>
                </PageBody>
              </CatalogPage>
              {Array.from(Array(otherPages)).map((_, pageIndex) => (
                <CatalogPage
                  key={`another-${pageIndex}`}
                  registration={registration}
                  theme={theme}
                  bleed={options.bleed}
                  {...rest}
                >
                  <PageBody>
                    <View style={{ marginTop: -4 }}>
                      <ExhibitorHeaderPage2
                        registration={registration}
                        style={{ marginBottom: 21 }}
                      />
                      <TableHeader />
                      <View style={{ height: 566, display: 'flex' }}>
                        {otherProducts
                          .slice(
                            pageIndex * productCountOtherPages,
                            pageIndex * productCountOtherPages +
                              productCountOtherPages
                          )
                          ?.map((registeredProduct, index) => (
                            <ProductRow
                              key={registeredProduct.productId}
                              registeredProduct={registeredProduct}
                              index={
                                pageIndex * productCountOtherPages +
                                productCountFirstPage +
                                index +
                                1
                              }
                              locale={locale}
                              isFirst={index === 0}
                            />
                          ))}
                      </View>
                    </View>
                  </PageBody>
                </CatalogPage>
              ))}
            </React.Fragment>
          );
        })}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

function paginateProducts({ products }) {
  // Squeeze up to 11 products on the first page
  if (products.length <= productCountFirstPage + 2) {
    return { firstPageProducts: products, otherProducts: [] };
  }

  // Otherwise, paginate as usual
  const firstPageProducts = products?.slice(0, productCountFirstPage);
  const otherProducts = products?.slice(productCountFirstPage);
  return { firstPageProducts, otherProducts };
}

function useBleed({ bleed = 15 } = { bleed: 15 }) {
  /**
   * @param {number} measurementInMm
   * @returns
   */
  function withBleed(measurementInMm, factor = 1) {
    return `${measurementInMm + bleed * factor}mm`;
  }
  return { withBleed };
}

function CatalogPage({
  registration,
  theme,
  bleed = 15, // Rediculously high number so that we recognize when it isn't set correctly
  style = {},
  children,
  ...rest
}) {
  const { withBleed } = useBleed({ bleed });

  return (
    <Page
      size={{ width: withBleed(210, 2), height: withBleed(276, 2) }}
      style={[
        // use paddingTop instead of paddingVertical to allow ABS page to extend a bit
        // This is because of the long list of countries in the header.
        { paddingTop: withBleed(12.5), paddingHorizontal: withBleed(20) },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {/* <Stencil
        src="https://res.cloudinary.com/bottlebooks/image/upload/v1658768869/pdf-objects/ABSWineAgencies/ABSStencil.png"
        style={{ height: '276mm', padding: `${bleed}mm` }}
      /> */}
      {/* <Stencil
        src="https://res.cloudinary.com/bottlebooks/image/upload/v1658778452/pdf-objects/ABSWineAgencies/ABSStencilPage2.png"
        style={{ height: '276mm', padding: `${bleed}mm` }}
      /> */}

      {children}

      <PageFooter registration={registration} />
    </Page>
  );
}

function PageFooter({ registration, ...rest }) {
  return (
    <View style={{ display: 'flex', borderTop: '1px solid black' }} {...rest}>
      <View
        style={{
          marginTop: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* <View style={{ width: '2.5cm', flexGrow: 0 }}>
          <Text variant="small">#ABSPortfolio</Text>
        </View> */}
        <QRCode
          url={`${registration.collection?.site?.publicUrl}/e/${registration.companyId}`}
          errorCorrectionLevel="L"
          margin={0}
          style={{
            width: '15mm',
            height: '15mm',
            marginTop: 2,
          }}
        />

        <View style={{ display: 'flex', flexDirection: 'row' }}>
          <View style={{ width: '2cm' }}>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <VeganIcon />
              <Text variant="small">Vegan</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <AwardsIcon />
              <Text variant="small">Awards</Text>
            </View>
          </View>
          <View style={{ width: '2.5cm' }}>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <VegetarianIcon />
              <Text variant="small">Vegetarian</Text>
            </View>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <SustainableIcon />
              <Text variant="small">Sustainable</Text>
            </View>
          </View>
          <View style={{ width: '2cm' }}>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <OrganicBiodynamicIcon />
              <Text variant="small">Organic/Biodynamic</Text>
            </View>
          </View>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text variant="small">Black: in ABS main assortiment</Text>
            <View
              style={{
                width: '2mm',
                height: 10,
                backgroundColor: colors.primary,
                marginLeft: '2mm',
              }}
            />
          </View>
          <View style={{ display: 'flex', flexDirection: 'row', marginTop: 3 }}>
            <Text variant="small">Available ex-cellar only</Text>
            <View
              style={{
                width: '2mm',
                height: 10,
                backgroundColor: colors.secondary,
                marginLeft: '2mm',
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

function ProductRow({ registeredProduct }) {
  const {
    alcohol,
    isBiodynamic,
    isCertifiedOrganic,
    isVegan,
    isVegetarian,
    awards,
    localCertifications,
    internationalCertifications,
  } = registeredProduct.product;
  const isInStock = !!registeredProduct.product.inventoryId;
  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '16.5mm',
        flexShrink: 1,
        justifyContent: 'space-between',
        paddingVertical: '1mm',
      }}
    >
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <View
          style={{
            paddingTop: 3,
            width: 482,
          }}
        >
          <Text style={{}}>{registeredProduct.product.name}</Text>
          <Text
            style={{
              color: '#888',
            }}
          >
            {registeredProduct.product.denomination}
          </Text>
        </View>
        <View
          style={{
            width: '3.8cm',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
              }}
            >
              <Text style={{ textAlign: 'right' }}>{alcohol || '–'}%</Text>
              <Text
                style={{
                  width: '2cm',
                  paddingTop: 3,
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                {registeredProduct?.rrpPricing?.value || '£0.00'}
              </Text>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: 5,
                justifyContent: 'flex-end',
              }}
            >
              {awards?.length > 0 && <AwardsIcon />}
              {isVegan && <VeganIcon />}
              {isVegetarian && <VegetarianIcon />}
              {(isBiodynamic || isCertifiedOrganic) && <VegetarianIcon />}
              {(localCertifications?.length > 0 ||
                internationalCertifications?.length > 0) && <SustainableIcon />}
            </View>
          </View>
          <View
            style={{
              backgroundColor: isInStock ? colors.primary : colors.secondary,
              width: '2mm',
              height: '100%',
              marginLeft: '2mm',
            }}
          />
        </View>
      </View>
    </View>
  );
}

function PageBody({ children, style = {}, ...rest }) {
  return (
    <View style={[{}, style]} {...rest}>
      {children}
    </View>
  );
}

function getUniqueCountries({ registration }) {
  const regions = registration.registeredProducts?.nodes?.map(
    ({ product }) => product.country
  );
  return regions.length > 0 ? [...new Set(regions)].sort().join(', ') : null;
}

function ExhibitorHeader({ registration, style, ...rest }) {
  return (
    <View
      style={[
        {
          display: 'flex',
          flexDirection: 'column',
          height: 175,
        },
        style,
      ]}
      {...rest}
    >
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <View style={{ display: 'flex' }}>
          <Title style={{ paddingRight: '2cm' }}>
            {registration.profile.name}
          </Title>

          <Text style={{ marginTop: 4 }}>
            {registration.profile.region ||
              getUniqueCountries({ registration })}
          </Text>
          <Text style={{ marginTop: 2 }}>
            Presented by:{' '}
            {registration.attendingContacts?.map((c) => c.fullName).join(', ')}
          </Text>
          <Text style={{ marginTop: 2 }}>{registration.profile.website}</Text>
          <Title style={{ marginTop: 4 }}>
            {registration.stand?.name || 'Table XYZ'}
          </Title>
        </View>
        <Company.ProfileLogo
          companyProfile={{
            logo: registration.profile.autoSizedLogo,
          }}
          height={70}
          width={200}
          gravity="north_east"
        />
      </View>

      <Text style={{ textAlign: 'justify', marginTop: '4mm' }}>
        {registration.profile.shortDescription?.replace(/(\r\n|\n|\r)/gm, '')}
      </Text>
    </View>
  );
}

function ExhibitorHeaderPage2({ registration, style, ...rest }) {
  return (
    <View
      style={[
        {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        },
        style,
      ]}
      {...rest}
    >
      <HeaderDetails
        registration={registration}
        style={{ marginTop: 2, width: '66%' }}
      />
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '30%',
        }}
      >
        <Company.ProfileLogo
          companyProfile={{
            logo: registration.profile.autoSizedLogo,
          }}
          height={60}
          width={140}
          gravity="north_east"
        />
      </View>
    </View>
  );
}

function TableHeader() {
  return (
    <View
      style={{
        borderBottom: `1px solid black`,
        display: 'flex',
        flexDirection: 'row',
        paddingBottom: 3,
      }}
    >
      <View style={{ flexGrow: 1 }} />
      <Text style={{ width: '2cm', textAlign: 'right' }}>ABV</Text>
      <Text style={{ width: '2cm', textAlign: 'right', marginRight: '4mm' }}>
        RRP
      </Text>
    </View>
  );
}

function HeaderDetails({ registration, ...rest }) {
  return (
    <View {...rest}>
      <Title style={{ paddingRight: '2cm' }}>{registration.profile.name}</Title>
      <Text style={{ marginTop: 4 }}>
        {registration.profile.region || getUniqueCountries({ registration })}
      </Text>
      <Title style={{ marginTop: 4 }}>
        {registration.stand?.name || 'Table XYZ'}
      </Title>
    </View>
  );
}

function Icon({ src, style }) {
  return (
    <Image
      src={src}
      style={[
        { height: 10, width: 10, marginRight: 2, marginBottom: 2 },
        style,
      ]}
    />
  );
}

function SustainableIcon({ style = {} } = {}) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1659442774/pdf-objects/ABSWineAgencies/Sustainable.png"
      style={style}
    />
  );
}
function VegetarianIcon({ style = {} } = {}) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1659442757/pdf-objects/ABSWineAgencies/Vegan.png"
      style={style}
    />
  );
}
function VeganIcon({ style = {} } = {}) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1659442757/pdf-objects/ABSWineAgencies/Vegan.png"
      style={style}
    />
  );
}
function OrganicBiodynamicIcon({ style = {} } = {}) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1659442766/pdf-objects/ABSWineAgencies/OrganicBiodynamic.png"
      style={style}
    />
  );
}
function AwardsIcon({ style = {} } = {}) {
  return (
    <Icon
      src="https://res.cloudinary.com/bottlebooks/image/upload/v1658782647/pdf-objects/ABSWineAgencies/AwardsNew.jpg"
      style={style}
    />
  );
}

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

const colors = {
  primary: '#A8C80F',
  secondary: '#53BEC9',
};

/** @type {Theme} */
export const theme = {
  colors,
  components: {
    Text: {
      base: { ...gothic400, fontSize: '10pt' },
      small: { ...gothic400, fontSize: '8pt' },
    },
    Title: {
      base: { ...gothic700, textTransform: 'uppercase' },
      small: {
        fontSize: 8,
        lineHeight: 1,
      },
      default: {
        fontSize: '14pt',
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

ABSWineAgenciesCatalogue.fragment = gql`
  fragment ABSWineAgenciesCatalogue on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...ABSWineAgenciesCatalogue_Table
    }
  }

  fragment ABSWineAgenciesCatalogue_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }

      collection {
        eventId: collectionId
        name
        site {
          publicUrl
        }
        logo {
          url
          fixed(width: 100, height: 20) {
            src
          }
        }
      }

      attendingContacts {
        fullName
      }

      profile {
        name
        street
        postalCode
        city
        country
        telephone
        email
        website
        shortDescription
        ...ProfileLogo
        mainImage {
          fixed(width: 500, height: 255, gravity: FACES) {
            src
          }
        }
        autoSizedLogo: logo {
          url
        }
      }

      registeredProducts {
        nodes {
          rrpPricing: customFieldValue(key: "rrpPricing") {
            ... on TextFieldValue {
              value
            }
          }
          producer {
            isSeekingDistribution
            distributionContacts {
              contactId
              companyName
            }
          }
          pricing {
            currency
            priceType
            price
          }
          productId
          product {
            productId
            name
            alcohol
            inventoryId

            regionHierarchy
            country
            denomination
            ...Blend
            ...BottleImage
            ... on Wine {
              awards {
                award
                medal
              }
              vineyard
              designation
              residualSugar
              totalAcidity
              isVegan
              isVegetarian
              isBiodynamic
              isCertifiedOrganic
              localCertifications {
                name
              }
              internationalCertifications {
                name
              }
            }
          }
        }
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
  ${Company.ProfileLogo.fragment}
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
